import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { expertLayout, type Expert, type ExpertType } from '../utils/expertProfile';

const TYPES: ExpertType[] = ['lawyer', 'inspector'];

interface ExpertFinderProps {
  type: ExpertType;
  onTypeChange: (type: ExpertType) => void;
  /** The available experts of the chosen type. */
  providers: Expert[];
  /** One card per expert, laid out as a wide card for one and rows for several. */
  renderExpert: (provider: Expert, layout: 'card' | 'row') => React.ReactNode;
  /** Keeps ids unique when the finder is on a page with another one. */
  idPrefix?: string;
  /** 'card' gives the dark card heading with an icon used on the project's other tabs. */
  heading?: 'section' | 'card';
}

/**
 * "Hitta hjälp" (ClickUp 869fh4y1m, 869fk17e6): choose a lawyer or an
 * inspector, search by name or place, and see the experts as cards. The same
 * section on Extern hjälp and in a project's Extern hjälp tab.
 */
export function ExpertFinder({ type, onTypeChange, providers, renderExpert, idPrefix = 'external-help', heading = 'section' }: ExpertFinderProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const choose = (next: ExpertType) => {
    onTypeChange(next);
    setQuery('');
  };
  // Radio group with arrow keys between the two kinds, as a native radio group.
  const onChoiceKey = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const next = type === 'lawyer' ? 'inspector' : 'lawyer';
    choose(next);
    document.getElementById(`${idPrefix}-choice-${next}`)?.focus();
  };
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return providers.filter(provider => !needle || [provider.name, provider.geographicArea, (provider as { city?: string }).city]
      .some(value => value?.toLowerCase().includes(needle)));
  }, [providers, query]);
  const layout = expertLayout(visible.length);

  return (
    <section className="card external-help-section" aria-labelledby={`${idPrefix}-find`}>
      {heading === 'card' ? (
        <>
          <div className="card-header">
            <h5 className="card-title mb-0" id={`${idPrefix}-find`}><i className="feather-search me-2" aria-hidden="true"></i>{t('externalHelp.find.title')}</h5>
          </div>
          <p className="external-help-section-lead" id={`${idPrefix}-question`}>{t('externalHelp.find.question')}</p>
        </>
      ) : (
        <div className="external-help-section-head">
          <h3 id={`${idPrefix}-find`}>{t('externalHelp.find.title')}</h3>
          <p id={`${idPrefix}-question`}>{t('externalHelp.find.question')}</p>
        </div>
      )}
      <div className="external-help-find">
        <div className="external-help-choices" role="radiogroup" aria-labelledby={`${idPrefix}-question`}>
          {TYPES.map(kind => (
            <button
              key={kind}
              id={`${idPrefix}-choice-${kind}`}
              type="button"
              role="radio"
              aria-checked={type === kind}
              tabIndex={type === kind ? 0 : -1}
              className="external-help-choice"
              onClick={() => choose(kind)}
              onKeyDown={onChoiceKey}
            >
              <span className="external-help-choice-icon" aria-hidden="true"><i className={kind === 'lawyer' ? 'feather-shield' : 'feather-clipboard'}></i></span>
              <span className="external-help-choice-text">
                <span className="external-help-choice-title">{t(`externalHelp.choice.${kind}.title`)}</span>
                <span className="external-help-choice-description">{t(`externalHelp.choice.${kind}.description`)}</span>
              </span>
              <span className="external-help-choice-check" aria-hidden="true"><i className="feather-check"></i></span>
            </button>
          ))}
        </div>

        <div className="external-help-listbar">
          <h4 data-testid="external-help-list-title">{t(`externalHelp.list.${type}`)} <span>({visible.length})</span></h4>
          <div className="external-help-search">
            <label htmlFor={`${idPrefix}-search`} className="visually-hidden">{t('externalHelp.search')}</label>
            <i className="feather-search" aria-hidden="true"></i>
            <input
              id={`${idPrefix}-search`}
              type="search"
              className="form-control"
              placeholder={t('externalHelp.search')}
              value={query}
              onChange={event => setQuery(event.target.value)}
            />
          </div>
        </div>
        <p className="external-help-cost"><i className="feather-info" aria-hidden="true"></i><span>{t('externalHelp.cost')}</span></p>

        {visible.length > 0 ? (
          <div className={`external-help-grid is-${layout}`}>{visible.map(provider => renderExpert(provider, layout))}</div>
        ) : (
          <p className="external-help-empty">{t(query.trim() ? 'externalHelp.noResults' : 'externalHelp.noProviders')}</p>
        )}
      </div>
    </section>
  );
}
